import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Card,
  CardContent,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,

  Modal,
  Fade,
  Backdrop
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  BarChart as ChartIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Close as CloseIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Settings as SettingsIcon,
  Edit as EditIcon,
  Logout as LogoutIcon,
  NotificationsActive as ReminderIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import api from '../config/api';
import OSPAuswertung from './OSPAuswertung';
import OSPBogenAnsicht from './OSPBogenAnsicht';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Tabpanel-Komponente für die Admin-Tabs
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 0.5, sm: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Vorschlag für die Gültigkeit neuer Fragebogen-Links (rund 6 Monate),
// entspricht DEFAULT_SESSION_EXPIRY im Backend
const DEFAULT_EXPIRES_DAYS = 182;

// Status-Farbzuordnung für Session-Status
const statusColors = {
  active: 'primary',
  completed: 'success',
  expired: 'error'
};

// Farben für die Diagramme

const OSPAdmin = () => {
  // Router hooks
  const location = useLocation();
  const navigate = useNavigate();
  
  const [tabValue, setTabValue] = useState(0);
  const [patients, setPatients] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [openQuestionnaireModal, setOpenQuestionnaireModal] = useState(false);
  
  // Dialog-States
  const [openNewPatientDialog, setOpenNewPatientDialog] = useState(false);
  const [openNewSessionDialog, setOpenNewSessionDialog] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Form-States
  const [newPatient, setNewPatient] = useState({
    patient_id: '',
    first_name: '',
    last_name: '',
    email: '',
    language: 'DE',
    date_of_birth: '',
    phone: '',
    gender: '',
    doctor_id: '',
    notes: ''
  });
  const [editPatient, setEditPatient] = useState(null);
  const [openEditPatientDialog, setOpenEditPatientDialog] = useState(false);
  const [newSession, setNewSession] = useState({
    patientUuid: '',
    expiresDays: DEFAULT_EXPIRES_DAYS
  });
  
  // Import/Export states
  const [importDialog, setImportDialog] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  
  // Settings states
  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [configChanged, setConfigChanged] = useState(false);
  const [tempConfig, setTempConfig] = useState(null);

  // Reminder states
  const [pendingSessions, setPendingSessions] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [reminderFilter, setReminderFilter] = useState({ daysSinceCreated: '', daysUntilExpiry: '' });
  const [selectedReminders, setSelectedReminders] = useState([]);
  const [sendingReminders, setSendingReminders] = useState(false);

  // API-Funktionen mit useCallback für stabile Referenzen
  const fetchPatientSessions = useCallback(async (uuid) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/admin/patients/${uuid}/sessions`);
      setSessions(response.data);
      setSelectedPatient(patients.find(p => p.uuid === uuid));
      setTabValue(1); // Zur Session-Übersicht wechseln
      return response.data; // Return data für Promise-Verkettung
    } catch (error) {
      console.error('Fehler beim Abrufen der Sessions:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [patients]);

  const fetchQuestionnaire = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      console.log(`Lade Fragebogen für Session ${sessionId}`);
      const response = await api.get(`/api/admin/sessions/${sessionId}/questionnaire`);
      console.log('Fragebogen-Antwort:', response.data);
      
      // Session-Informationen speichern
      const session = sessions.find(s => s.session_id === sessionId);
      setSelectedSession(session);
      
      if (!response.data.exists) {
        // Wenn kein Fragebogen existiert, zeigen wir eine Meldung an
        setQuestionnaire({
          data: { message: "Für diese Session wurde noch kein Fragebogen ausgefüllt." },
          exists: false
        });
      } else {
        // Normaler Fall - Fragebogen existiert
        setQuestionnaire(response.data);
      }
      
      // Modal öffnen
      setOpenQuestionnaireModal(true);
    } catch (error) {
      console.error('Fehler beim Abrufen des Fragebogens:', error);
      alert('Der Fragebogen konnte nicht geladen werden: ' + 
            (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  }, [sessions]);

  // Beim ersten Laden Patienten abrufen
  useEffect(() => {
    fetchPatients();
  }, []);
  
  // URL-Parameter verarbeiten für direkten Link
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const patientUuid = params.get('patient');
    const sessionId = params.get('session');
    
    if (patientUuid && sessionId && patients.length > 0) {
      // Finde den Patient
      const patient = patients.find(p => p.uuid === patientUuid);
      if (patient) {
        setSelectedPatient(patient);
        // Lade die Sessions für diesen Patienten
        fetchPatientSessions(patientUuid).then(() => {
          // Wechsle zum Sessions-Tab
          setTabValue(1);
          // Öffne automatisch den spezifischen Fragebogen
          setTimeout(() => {
            fetchQuestionnaire(sessionId);
          }, 500);
        });
        
        // Entferne die Parameter aus der URL
        navigate('/admin', { replace: true });
      }
    }
  }, [patients, location.search, fetchPatientSessions, fetchQuestionnaire, navigate]);

  // Tab-Wechsel
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Fragebogen zurücksetzen beim Tab-Wechsel
    if (newValue !== 1) {
      setQuestionnaire(null);
      setSelectedSession(null);
    }
  };

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');  // This will trigger the ProtectedRoute to show login
  };

  // API-Funktionen
  const fetchPatients = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/admin/patients');
      setPatients(response.data);
    } catch (error) {
      console.error('Fehler beim Abrufen der Patienten:', error);
    } finally {
      setLoading(false);
    }
  };

  const sendNotificationEmail = async (sessionId) => {
    console.log(`Sende E-Mail-Benachrichtigung für Session ${sessionId}`);
    setLoading(true);
    try {
      await api.post(`/api/admin/sessions/${sessionId}/notify`);
      alert('E-Mail wurde erfolgreich versendet');
    } catch (error) {
      console.error('Fehler beim Senden der E-Mail:', error);
      alert('Fehler beim Senden der E-Mail: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Reminder-Funktionen
  const fetchPendingSessions = async () => {
    setPendingLoading(true);
    try {
      let url = '/api/admin/sessions/pending';
      const params = new URLSearchParams();

      if (reminderFilter.daysSinceCreated) {
        params.append('days_since_created', reminderFilter.daysSinceCreated);
      }
      if (reminderFilter.daysUntilExpiry) {
        params.append('days_until_expiry', reminderFilter.daysUntilExpiry);
      }

      if (params.toString()) {
        url += '?' + params.toString();
      }

      const response = await api.get(url);
      setPendingSessions(response.data.sessions || []);
      setSelectedReminders([]); // Reset selection
    } catch (error) {
      console.error('Fehler beim Abrufen der unbeantworteten Fragebögen:', error);
      alert('Fehler beim Laden: ' + (error.response?.data?.detail || error.message));
    } finally {
      setPendingLoading(false);
    }
  };

  const sendReminders = async (sessionIds = null) => {
    // Ein Klick verschickt echte Mails an Familien – vorher Anzahl nennen und bestätigen lassen
    const anzahl = sessionIds && sessionIds.length > 0 ? sessionIds.length : pendingSessions.length;
    if (!window.confirm(`Erinnerung jetzt an ${anzahl} Empfänger senden?\n\nDie automatischen Erinnerungen (Tag 7, 14, 28) laufen unabhängig davon weiter.`)) {
      return;
    }
    setSendingReminders(true);
    try {
      const payload = {};

      if (sessionIds && sessionIds.length > 0) {
        payload.session_ids = sessionIds;
      } else if (reminderFilter.daysSinceCreated) {
        payload.days_since_created = parseInt(reminderFilter.daysSinceCreated);
      }
      if (reminderFilter.daysUntilExpiry) {
        payload.days_until_expiry = parseInt(reminderFilter.daysUntilExpiry);
      }

      const response = await api.post('/api/admin/sessions/send-reminders', payload);

      alert(`Erinnerungen gesendet!\n\nGeplant: ${response.data.reminders_scheduled}\nFehlgeschlagen: ${response.data.reminders_failed}`);

      // Aktualisiere die Liste
      fetchPendingSessions();
    } catch (error) {
      console.error('Fehler beim Senden der Erinnerungen:', error);
      alert('Fehler beim Senden: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingReminders(false);
    }
  };

  const toggleReminderSelection = (sessionId) => {
    setSelectedReminders(prev =>
      prev.includes(sessionId)
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const selectAllReminders = () => {
    if (selectedReminders.length === pendingSessions.length) {
      setSelectedReminders([]);
    } else {
      setSelectedReminders(pendingSessions.map(s => s.session_id));
    }
  };

  const createPatient = async () => {
    setLoading(true);
    try {
      // Logging der zu sendenden Daten
      console.log("Sende Patientendaten:", newPatient);
      
      const response = await api.post('/api/admin/patients', newPatient);
      
      console.log("Serverantwort:", response.data);
      
      setOpenNewPatientDialog(false);
      
      // Aktualisiere die Patientenliste nach dem Erstellen
      await fetchPatients();
      
      resetNewPatient();
      
      // Erfolgsmeldung anzeigen
      alert(`Patient wurde erfolgreich erstellt. UUID: ${response.data.uuid}`);
    } catch (error) {
      console.error('Fehler beim Erstellen des Patienten:', error);
      alert(`Fehler beim Erstellen des Patienten: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const createSession = async () => {
    setLoading(true);
    try {
      const response = await api.post('/api/admin/sessions/create', newSession);
      setOpenNewSessionDialog(false);
      fetchPatientSessions(newSession.patientUuid);
      handleCopyLink(response.data.link);
      resetNewSession();
    } catch (error) {
      console.error('Fehler beim Erstellen der Session:', error);
    } finally {
      setLoading(false);
    }
  };

  // Hilfsfunktionen
  const resetNewPatient = () => {
    setNewPatient({
      patient_id: '',
      first_name: '',
      last_name: '',
      email: '',
      language: 'DE',
      date_of_birth: '',
      phone: '',
      gender: '',
      doctor_id: '',
      notes: ''
    });
  };

  const updatePatient = async () => {
    setLoading(true);
    try {
      await api.put(`/api/admin/patients/${editPatient.uuid}`, editPatient);
      setOpenEditPatientDialog(false);
      fetchPatients();
      setEditPatient(null);
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Patienten:', error);
      alert(`Fehler beim Aktualisieren des Patienten: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (patient) => {
    setEditPatient({ ...patient });
    setOpenEditPatientDialog(true);
  };

  const resetNewSession = () => {
    setNewSession({
      patientUuid: '',
      expiresDays: DEFAULT_EXPIRES_DAYS
    });
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 3000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return format(parseISO(dateString), 'dd.MM.yyyy HH:mm', { locale: de });
  };

  // Fragebogen als PDF herunterladen
  const handleDownloadPDF = async () => {
    if (!questionnaire || !selectedSession || !selectedPatient) {
      alert('Keine Fragebogendaten verfügbar');
      return;
    }

    try {
      // Erstelle ein temporäres Element für die PDF-Generierung
      const printContent = document.getElementById('questionnaire-content');
      if (!printContent) {
        console.error('Element questionnaire-content nicht gefunden');
        alert('PDF-Element nicht gefunden. Bitte öffnen Sie zuerst einen Fragebogen.');
        return;
      }

      console.log('Starte PDF-Generierung...');

      // Temporär das Element für bessere PDF-Generierung anpassen
      const originalStyle = printContent.style.cssText;
      printContent.style.width = '800px';
      printContent.style.padding = '20px';
      printContent.style.backgroundColor = 'white';

      // Erstelle Canvas vom HTML-Inhalt
      console.log('Erstelle Canvas...');
      const canvas = await html2canvas(printContent, {
        scale: 2,
        useCORS: true,
        logging: false,
        windowWidth: 850,
        scrollY: -window.scrollY,
        backgroundColor: '#ffffff'
      });

      console.log('Canvas erstellt, Größe:', canvas.width, 'x', canvas.height);
      
      // Stelle Original-Style wieder her
      printContent.style.cssText = originalStyle;
      
      // Erstelle PDF
      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = 10; // Rand in mm
      const imgWidth = 210 - (2 * margin); // A4 width minus margins
      const pageHeight = 297 - (2 * margin); // A4 height minus margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = margin;
      
      // Füge das Bild zum PDF hinzu
      const imgData = canvas.toDataURL('image/png');
      pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
      
      // Füge weitere Seiten hinzu, falls nötig
      while (heightLeft > 0) {
        position = heightLeft - imgHeight + margin;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', margin, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Speichere das PDF
      const patientName = selectedPatient?.name || `${selectedPatient?.first_name || ''} ${selectedPatient?.last_name || ''}`.trim() || 'Patient';
      const fileName = `Fragebogen_${patientName.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, '_')}_${selectedSession.session_id.substring(0, 8)}_${format(new Date(), 'yyyy-MM-dd')}.pdf`;

      console.log('Speichere PDF als:', fileName);
      pdf.save(fileName);

      console.log('PDF erfolgreich erstellt und heruntergeladen');
      // Optional: Success message
      // alert('PDF wurde erfolgreich erstellt und heruntergeladen');

    } catch (error) {
      console.error('Fehler beim Erstellen des PDFs:', error);
      alert('Fehler beim Erstellen des PDFs: ' + error.message);
    }
  };

  // Import/Export functions
  const handleImport = async () => {
    if (!importFile) {
      alert('Bitte wählen Sie eine Datei aus.');
      return;
    }
    
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      
      const response = await api.post('/api/admin/patients/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setImportResult(response.data);
      setImportFile(null);
      
      // Refresh patient list
      await fetchPatients();
      
    } catch (error) {
      console.error('Import error:', error);
      alert('Fehler beim Import: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };
  
  const handleExport = async () => {
    try {
      const response = await api.get('/api/admin/patients/export', {
        responseType: 'blob',
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `patienten_export_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
    } catch (error) {
      console.error('Export error:', error);
      alert('Fehler beim Export: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Settings functions
  const fetchConfig = async () => {
    setConfigLoading(true);
    try {
      const configResponse = await api.get('/api/admin/config');
      const data = configResponse.data;
      
      // Map nested config structure to flat structure for the UI
      const flatConfig = {
        // Email settings
        EMAIL_HOST: data.email?.host,
        EMAIL_PORT: data.email?.port,
        EMAIL_USER: data.email?.user,
        EMAIL_PASS: data.email?.password,
        EMAIL_FROM: data.email?.from,
        EMAIL_SECURE: data.email?.secure,
        ADMIN_EMAIL: data.email?.admin_email,
        
        // Institute settings
        INSTITUTE_NAME: data.institute?.name,
        INSTITUTE_FULL_NAME: data.institute?.full_name,
        INSTITUTE_SUBTITLE: data.institute?.subtitle,
        INSTITUTE_DESCRIPTION: data.institute?.description,
        INSTITUTE_OWNER: data.institute?.owner,
        INSTITUTE_SCIENTIFIC_LEAD: data.institute?.scientific_lead,
        INSTITUTE_ADDRESS: data.institute?.address,
        INSTITUTE_CITY: data.institute?.city,
        INSTITUTE_COUNTRY: data.institute?.country,
        INSTITUTE_IK: data.institute?.ik,
        INSTITUTE_PHONE1: data.institute?.phone1,
        INSTITUTE_PHONE2: data.institute?.phone2,
        INSTITUTE_FAX: data.institute?.fax,
        INSTITUTE_EMAIL: data.institute?.email,
        INSTITUTE_WEBSITE: data.institute?.website,
        
        // App settings
        DEFAULT_SESSION_EXPIRY: data.app?.default_session_expiry,
        
        // Keep original structure for reference
        _original: data
      };
      
      setConfig(flatConfig);
    } catch (error) {
      console.error('Error fetching config:', error);
      alert('Fehler beim Laden der Konfiguration: ' + (error.response?.data?.detail || error.message));
    } finally {
      setConfigLoading(false);
    }
  };

  const handleTestEmail = async (email) => {
    try {
      await api.post('/api/admin/test-email', { test_email: email });
      alert(`Test-E-Mail wurde an ${email} gesendet`);
    } catch (error) {
      console.error('Error sending test email:', error);
      alert('Fehler beim Senden der Test-E-Mail');
    }
  };

  // Fragebogen drucken
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const printContent = document.getElementById('questionnaire-content');

    if (!printWindow || !printContent) return;

    // Die Einzelanzeige ist mit MUI gestaltet. Im Produktions-Build stehen deren Regeln nur im CSSOM
    // (leere <style>-Tags), deshalb die Regeln auslesen statt die Tags zu kopieren.
    const css = Array.from(document.styleSheets).map((sheet) => {
      try {
        return Array.from(sheet.cssRules).map((regel) => regel.cssText).join('\n');
      } catch (e) {
        return '';
      }
    }).join('\n');
    const name = selectedPatient?.name || `${selectedPatient?.first_name || ''} ${selectedPatient?.last_name || ''}`.trim() || 'Patient';

    printWindow.document.write(`<!DOCTYPE html>
      <html lang="de">
        <head>
          <meta charset="utf-8">
          <title>OSP-Fragebogen – ${name}</title>
          <style>${css}</style>
          <style>
            @page { size: A4; margin: 16mm; }
            body { margin: 0; background: #fff; font-family: Roboto, Helvetica, Arial, sans-serif; }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          </style>
        </head>
        <body>${printContent.innerHTML}</body>
      </html>`);
    printWindow.document.close();

    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 300);
  };


  // Fragebogen per E-Mail an Admin senden
  const handleSendReport = async () => {
    if (!questionnaire || !selectedSession) return;
    
    try {
      // In einer echten Anwendung würde hier ein API-Endpunkt aufgerufen
      alert('Bericht wird an Admin gesendet...');
    } catch (error) {
      console.error('Fehler beim Senden des Berichts:', error);
      alert('Fehler beim Senden des Berichts');
    }
  };

  // Modal schließen
  const handleCloseModal = () => {
    setOpenQuestionnaireModal(false);
    setQuestionnaire(null);
    setSelectedSession(null);
  };

  // Filter-Funktion für Patienten-Suche
  const filteredPatients = patients.filter(patient => {
    const searchTermLower = searchTerm?.toLowerCase() || '';
    
    // Sichere Prüfung aller Felder
    const nameMatches = patient.name?.toLowerCase()?.includes(searchTermLower) || false;
    const firstNameMatches = patient.first_name?.toLowerCase()?.includes(searchTermLower) || false;
    const lastNameMatches = patient.last_name?.toLowerCase()?.includes(searchTermLower) || false;
    const emailMatches = patient.email?.toLowerCase()?.includes(searchTermLower) || false;
    const uuidMatches = patient.uuid?.toLowerCase()?.includes(searchTermLower) || false;
    
    return nameMatches || firstNameMatches || lastNameMatches || emailMatches || uuidMatches;
  });

  // Render-Funktionen für die Tabs
  const renderPatientsList = () => (
    <>
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        justifyContent: 'space-between', 
        mb: 3, 
        gap: 2,
        alignItems: 'center'
      }}>
        <Typography variant="h5" sx={{ whiteSpace: 'nowrap' }}>
          Patientenliste ({searchTerm ? `${filteredPatients.length} von ${patients.length}` : patients.length})
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          flex: '1 1 320px',
          justifyContent: 'flex-end',
          gap: 1
        }}>
          <TextField
            placeholder="Suchen..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flex: '1 1 200px', maxWidth: { sm: 320 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Tooltip title="Aktualisieren">
              <IconButton onClick={fetchPatients} aria-label="Aktualisieren">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={() => setImportDialog(true)}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Import
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Export
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenNewPatientDialog(true)}
              sx={{ flex: { xs: 1, sm: 'none' } }}
            >
              Neuer Patient
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Desktop-Ansicht: Tabelle */}
      <Box sx={{ display: { xs: 'none', md: 'block' } }}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Geburtsdatum</TableCell>
                <TableCell>Angelegt</TableCell>
                <TableCell>Fragebögen</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredPatients.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Keine Patienten gefunden
                  </TableCell>
                </TableRow>
              ) : (
                filteredPatients.map((patient) => (
                  <TableRow key={patient.uuid} hover>
                    <TableCell sx={{ color: 'text.secondary' }}>{patient.patient_id || '–'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {patient.name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || '–'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {patient.email || 'keine E-Mail'}{patient.language && patient.language !== 'DE' ? ` · ${patient.language}` : ''}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{patient.date_of_birth ? new Date(patient.date_of_birth).toLocaleDateString('de-DE') : '–'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{patient.created_at ? new Date(patient.created_at).toLocaleDateString('de-DE') : '–'}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(patient.completed || 0) > 0 && (
                          <Chip size="small" color="success" variant="outlined" label={`${patient.completed} ausgefüllt`} />
                        )}
                        {(patient.sessions || 0) - (patient.completed || 0) > 0 && (
                          <Chip size="small" variant="outlined" label={`${(patient.sessions || 0) - (patient.completed || 0)} offen`} />
                        )}
                        {!(patient.sessions > 0) && <Typography variant="caption" color="text.secondary">keiner</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Tooltip title="Patient bearbeiten">
                          <IconButton size="small" onClick={() => openEditDialog(patient)} aria-label="Patient bearbeiten">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setSelectedPatient(patient);
                            fetchPatientSessions(patient.uuid);
                            setTabValue(1);
                          }}
                          startIcon={<ViewIcon />}
                          sx={{ whiteSpace: 'nowrap' }}
                        >
                          Bögen
                        </Button>
                        <Tooltip title="Neuen Fragebogen anlegen">
                          <IconButton 
                            size="small"
                            aria-label="Neuen Fragebogen anlegen"
                            onClick={() => {
                              setSelectedPatient(patient);
                              setNewSession({ ...newSession, patientUuid: patient.uuid });
                              setOpenNewSessionDialog(true);
                            }}
                            color="primary"
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Mobile-Ansicht: Cards */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredPatients.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Keine Patienten gefunden</Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filteredPatients.map((patient) => (
              <Grid item xs={12} key={patient.uuid}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {patient.name || `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || 'Kein Name'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {patient.email || 'Keine E-Mail'}
                    </Typography>
                    {patient.patient_id && (
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Patienten-ID: {patient.patient_id}
                      </Typography>
                    )}
                    {patient.date_of_birth && (
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Geburtsdatum: {new Date(patient.date_of_birth).toLocaleDateString('de-DE')}
                      </Typography>
                    )}
                    {patient.phone && (
                      <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                        Telefon: {patient.phone}
                      </Typography>
                    )}
                    <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
                      Sprache: {patient.language || 'DE'}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mb: 1 }}>
                      Erstellt: {formatDate(patient.created_at)}
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Chip 
                          label={`${patient.sessions || 0} Fragebögen`} 
                          size="small" 
                          color="primary" 
                          variant="outlined"
                        />
                        <Chip 
                          label={`${patient.completed || 0} abgeschlossen`} 
                          size="small" 
                          color="success" 
                          variant="outlined"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="medium"
                          onClick={() => openEditDialog(patient)}
                          startIcon={<EditIcon />}
                        >
                          Patient bearbeiten
                        </Button>
                        <Button
                          variant="contained"
                          fullWidth
                          size="medium"
                          onClick={() => {
                            setSelectedPatient(patient);
                            fetchPatientSessions(patient.uuid);
                            setTabValue(1);
                          }}
                          startIcon={<AssignmentIcon />}
                        >
                          Fragebögen anzeigen
                        </Button>
                        <Button
                          variant="outlined"
                          fullWidth
                          size="medium"
                          onClick={() => {
                            setSelectedPatient(patient);
                            setNewSession({ ...newSession, patientUuid: patient.uuid });
                            setOpenNewSessionDialog(true);
                          }}
                          startIcon={<AddIcon />}
                        >
                          Neuen Fragebogen erstellen
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </>
  );

  const renderSessionsList = () => {
    // Wenn kein Patient ausgewählt ist, hilfreiche Meldung anzeigen
    if (!selectedPatient) {
      return (
        <Box sx={{ mt: 4, p: 3, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Bitte wählen Sie zuerst einen Patienten aus der Patientenliste
          </Typography>
          <Button
            variant="contained"
            onClick={() => setTabValue(0)}
            sx={{ mt: 2 }}
          >
            Zur Patientenliste
          </Button>
        </Box>
      );
    }
  
    return (
      <>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between', 
          mb: 3, 
          gap: 2
        }}>
          <Box>
            <Typography variant="h5">
              Fragebögen für {selectedPatient?.name || `${selectedPatient?.first_name || ''} ${selectedPatient?.last_name || ''}`.trim() || selectedPatient?.uuid || 'Patienten'}
            </Typography>
            {selectedPatient && (
              <Typography variant="subtitle2" color="textSecondary">
                {selectedPatient.email ? `E-Mail: ${selectedPatient.email}` : ''}
                {selectedPatient.email && selectedPatient.uuid ? ' | ' : ''}
                {selectedPatient.uuid ? `UUID: ${selectedPatient.uuid}` : ''}
              </Typography>
            )}
          </Box>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1
          }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => fetchPatientSessions(selectedPatient.uuid)}
              size="small"
              sx={{ display: { xs: 'none', sm: 'flex' } }}
            >
              Aktualisieren
            </Button>
            <Button
              variant="outlined"
              onClick={() => setTabValue(0)}
              size="small"
            >
              Zurück
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewSession({ ...newSession, patientUuid: selectedPatient.uuid });
                setOpenNewSessionDialog(true);
              }}
              size="small"
            >
              Neuer Fragebogen
            </Button>
          </Box>
        </Box>
  
        {/* Desktop-Ansicht: Tabelle */}
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Fragebogen ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Erstellt am</TableCell>
                  <TableCell>Läuft ab am</TableCell>
                  <TableCell>Letzte Aktivität</TableCell>
                  <TableCell>Erinnerungen</TableCell>
                  <TableCell>Aktionen</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : sessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Keine Fragebögen gefunden
                    </TableCell>
                  </TableRow>
                ) : (
                  sessions.map((session) => (
                    <TableRow 
                      key={session.session_id}
                      sx={{ 
                        backgroundColor: selectedSession?.session_id === session.session_id ? 'action.selected' : 'inherit',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                    >
                      <TableCell>{session.session_id}</TableCell>
                      <TableCell>
                        <Chip 
                          label={session.status} 
                          color={statusColors[session.status] || "default"} 
                          size="small" 
                        />
                      </TableCell>
                      <TableCell>{formatDate(session.created_at)}</TableCell>
                      <TableCell>{formatDate(session.expires_at)}</TableCell>
                      <TableCell>{formatDate(session.last_activity)}</TableCell>
                      <TableCell>
                        {session.reminder_count
                          ? (
                            <Tooltip title={`Zuletzt erinnert: ${formatDate(session.last_reminder_at)}`}>
                              <Chip label={`${session.reminder_count}×`} size="small" color="warning" variant="outlined" />
                            </Tooltip>
                          )
                          : <Typography variant="caption" color="text.secondary">–</Typography>}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={session.status === 'completed' ? "Fragebogen anzeigen" : "Fragebogen prüfen (noch nicht ausgefüllt)"}>
                          <IconButton 
                            onClick={() => fetchQuestionnaire(session.session_id)}
                            color={session.status === 'completed' ? "primary" : "default"}
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Link kopieren">
                          <IconButton onClick={() => handleCopyLink(`${window.location.origin}/fragebogen/${selectedPatient.uuid}/${session.session_id}`)}>
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                        {selectedPatient.email && (
                          <Tooltip title="Link per E-Mail senden">
                            <IconButton onClick={() => sendNotificationEmail(session.session_id)}>
                              <SendIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Mobile/Tablet-Ansicht: Cards */}
        <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : sessions.length === 0 ? (
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography>Keine Fragebögen gefunden</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {sessions.map((session) => (
                <Grid item xs={12} key={session.session_id}>
                  <Card 
                    sx={{ 
                      border: selectedSession?.session_id === session.session_id ? 2 : 0,
                      borderColor: 'primary.main',
                      transition: 'all 0.3s ease',
                      '&:hover': { boxShadow: 3 }
                    }}
                  >
                    <CardContent>
                      {/* Header mit Status */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Chip 
                          label={session.status === 'completed' ? 'Abgeschlossen' : session.status === 'active' ? 'Aktiv' : 'Abgelaufen'} 
                          color={statusColors[session.status] || "default"} 
                          size="medium"
                          sx={{ fontWeight: 'bold' }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(session.created_at)}
                        </Typography>
                      </Box>

                      {/* Fragebogen Info */}
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        ID: {session.session_id.substring(0, 8)}...
                      </Typography>

                      {/* Ablaufdatum */}
                      {session.expires_at && (
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Läuft ab:</strong> {formatDate(session.expires_at)}
                        </Typography>
                      )}

                      {/* Erinnerungs-Protokoll */}
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        <strong>Erinnerungen:</strong>{' '}
                        {session.reminder_count
                          ? `${session.reminder_count}× (zuletzt ${formatDate(session.last_reminder_at)})`
                          : 'noch keine'}
                      </Typography>

                      {/* Action Buttons - Modern und Touch-freundlich */}
                      <Box sx={{ display: 'flex', gap: 1, flexDirection: 'column' }}>
                        <Button
                          variant={session.status === 'completed' ? "contained" : "outlined"}
                          startIcon={<ViewIcon />}
                          onClick={() => fetchQuestionnaire(session.session_id)}
                          fullWidth
                          size="medium"
                        >
                          {session.status === 'completed' ? 'Fragebogen anzeigen' : 'Status prüfen'}
                        </Button>
                        
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            startIcon={<CopyIcon />}
                            onClick={() => {
                              const link = `${window.location.origin}/fragebogen/${selectedPatient.uuid}/${session.session_id}`;
                              handleCopyLink(link);
                            }}
                            size="small"
                            sx={{ flex: 1 }}
                          >
                            Link kopieren
                          </Button>
                          
                          {selectedPatient.email && (
                            <Button
                              variant="outlined"
                              startIcon={<SendIcon />}
                              onClick={() => sendNotificationEmail(session.session_id)}
                              size="small"
                              sx={{ flex: 1 }}
                            >
                              E-Mail senden
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </>
    );
  };
  
  const renderQuestionnaireDetails = () => {
    if (!questionnaire) {
      return null;
    }
  
    // Prüfen, ob es sich um einen leeren Fragebogen handelt
    if (!questionnaire.exists) {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Kein Fragebogen vorhanden
          </Typography>
          <Typography paragraph>
            Dieser Fragebogen wurde noch nicht ausgefüllt.
          </Typography>
          <Typography paragraph>
            Der Patient kann über den Link auf den Fragebogen zugreifen und diesen ausfüllen.
          </Typography>
        </Paper>
      );
    }
  
    // Zugriff auf die Daten
    let data = questionnaire.data;
    
    // Sicherstellen, dass alle benötigten Objekte existieren
    if (!data || typeof data !== 'object') {
      return (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Fehler beim Laden der Daten
          </Typography>
          <Typography paragraph>
            Die Fragebogendaten konnten nicht korrekt geladen werden.
          </Typography>
        </Paper>
      );
    }
    
    // Sicherstellen, dass alle Unterobjekte existieren
    data.personendaten = data.personendaten || {};
    data.gruende = data.gruende || {};
    data.angewohnheiten = data.angewohnheiten || {};
    data.verbesserungen = data.verbesserungen || {};

    return (
      <OSPBogenAnsicht
        data={data}
        patient={selectedPatient}
        session={selectedSession}
        submittedAt={questionnaire?.submitted_at}
      />
    );
  };

  // Load config when settings tab is first opened
  useEffect(() => {
    if (tabValue === 3 && !config && !configLoading) {
      fetchConfig();
    }
  }, [tabValue, config, configLoading]);

  // Load pending sessions when reminders tab is first opened
  useEffect(() => {
    if (tabValue === 4 && pendingSessions.length === 0 && !pendingLoading) {
      fetchPendingSessions();
    }
  }, [tabValue, pendingSessions.length, pendingLoading]);

  // Initialize temp config when config loads
  useEffect(() => {
    if (config && !tempConfig) {
      setTempConfig({ ...config });
    }
  }, [config, tempConfig]);

  const renderSettings = () => {

    const handleConfigChange = (key, value) => {
      setTempConfig(prev => ({
        ...prev,
        [key]: value
      }));
      setConfigChanged(true);
    };

    const saveConfig = async () => {
      if (!tempConfig) return;
      
      setSaving(true);
      try {
        // Map flat structure back to nested structure for backend
        const nestedConfig = {
          email: {
            host: tempConfig.EMAIL_HOST,
            port: tempConfig.EMAIL_PORT ? parseInt(tempConfig.EMAIL_PORT) : 587,
            user: tempConfig.EMAIL_USER,
            password: tempConfig.EMAIL_PASS,
            from: tempConfig.EMAIL_FROM,
            secure: tempConfig.EMAIL_SECURE === 'true' || tempConfig.EMAIL_SECURE === true,
            admin_email: tempConfig.ADMIN_EMAIL,
            enabled: true
          },
          institute: {
            name: tempConfig.INSTITUTE_NAME,
            full_name: tempConfig.INSTITUTE_FULL_NAME,
            subtitle: tempConfig.INSTITUTE_SUBTITLE,
            description: tempConfig.INSTITUTE_DESCRIPTION,
            owner: tempConfig.INSTITUTE_OWNER,
            scientific_lead: tempConfig.INSTITUTE_SCIENTIFIC_LEAD,
            address: tempConfig.INSTITUTE_ADDRESS,
            city: tempConfig.INSTITUTE_CITY,
            country: tempConfig.INSTITUTE_COUNTRY,
            ik: tempConfig.INSTITUTE_IK,
            phone1: tempConfig.INSTITUTE_PHONE1,
            phone2: tempConfig.INSTITUTE_PHONE2,
            fax: tempConfig.INSTITUTE_FAX,
            email: tempConfig.INSTITUTE_EMAIL,
            website: tempConfig.INSTITUTE_WEBSITE
          },
          app: {
            default_session_expiry: tempConfig.DEFAULT_SESSION_EXPIRY ? parseInt(tempConfig.DEFAULT_SESSION_EXPIRY) : DEFAULT_EXPIRES_DAYS,
            enable_patient_import: true,
            enable_analytics: true,
            enable_pdf_export: true
          }
        };
        
        await api.put('/api/admin/config', nestedConfig);
        setConfig(tempConfig);
        setConfigChanged(false);
        alert('Konfiguration wurde gespeichert');
      } catch (error) {
        console.error('Error saving config:', error);
        alert('Fehler beim Speichern der Konfiguration: ' + (error.response?.data?.detail || error.message));
      } finally {
        setSaving(false);
      }
    };

    const resetConfig = () => {
      if (config) {
        setTempConfig({ ...config });
        setConfigChanged(false);
      }
    };

    const handleTestEmailSend = async () => {
      if (!testEmail || !testEmail.includes('@')) {
        alert('Bitte geben Sie eine gültige E-Mail-Adresse ein');
        return;
      }
      
      try {
        await handleTestEmail(testEmail);
        setTestEmail('');
      } catch (error) {
        // Error handling is done in handleTestEmail function
      }
    };

    if (configLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Lade Einstellungen...</Typography>
        </Box>
      );
    }

    if (!config || !tempConfig) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Einstellungen konnten nicht geladen werden
          </Typography>
          <Button variant="contained" onClick={fetchConfig}>
            Erneut versuchen
          </Button>
        </Box>
      );
    }

    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Einstellungen</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {configChanged && (
              <Button
                variant="outlined"
                onClick={resetConfig}
                disabled={saving}
              >
                Zurücksetzen
              </Button>
            )}
            <Button
              variant="contained"
              onClick={saveConfig}
              disabled={!configChanged || saving}
            >
              {saving ? <CircularProgress size={24} /> : 'Speichern'}
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* E-Mail Konfiguration */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  E-Mail Konfiguration
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SMTP Server"
                      fullWidth
                      value={tempConfig.EMAIL_HOST || ''}
                      onChange={(e) => handleConfigChange('EMAIL_HOST', e.target.value)}
                      helperText="z.B. mail.example.com"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="SMTP Port"
                      type="number"
                      fullWidth
                      value={tempConfig.EMAIL_PORT || ''}
                      onChange={(e) => handleConfigChange('EMAIL_PORT', e.target.value)}
                      helperText="z.B. 587 oder 465"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="E-Mail Benutzername"
                      fullWidth
                      value={tempConfig.EMAIL_USER || ''}
                      onChange={(e) => handleConfigChange('EMAIL_USER', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="E-Mail Passwort"
                      type="password"
                      fullWidth
                      value={tempConfig.EMAIL_PASS || ''}
                      onChange={(e) => handleConfigChange('EMAIL_PASS', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Absender E-Mail"
                      fullWidth
                      value={tempConfig.EMAIL_FROM || ''}
                      onChange={(e) => handleConfigChange('EMAIL_FROM', e.target.value)}
                      helperText="E-Mail-Adresse, die als Absender angezeigt wird"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Admin E-Mail"
                      fullWidth
                      value={tempConfig.ADMIN_EMAIL || ''}
                      onChange={(e) => handleConfigChange('ADMIN_EMAIL', e.target.value)}
                      helperText="E-Mail für administrative Benachrichtigungen"
                    />
                  </Grid>
                </Grid>

                {/* E-Mail Test Section */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="subtitle1" gutterBottom>
                    E-Mail Konfiguration testen
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <TextField
                      label="Test E-Mail Adresse"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      sx={{ flex: 1 }}
                      helperText="Eine Test-E-Mail wird an diese Adresse gesendet"
                    />
                    <Button
                      variant="outlined"
                      onClick={handleTestEmailSend}
                      disabled={!testEmail}
                    >
                      Test senden
                    </Button>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Institut Konfiguration */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Institut Informationen
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Institut Name (Kurz)"
                      fullWidth
                      value={tempConfig.INSTITUTE_NAME || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_NAME', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Institut Name (Vollständig)"
                      fullWidth
                      value={tempConfig.INSTITUTE_FULL_NAME || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_FULL_NAME', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Institut Beschreibung"
                      fullWidth
                      multiline
                      rows={2}
                      value={tempConfig.INSTITUTE_DESCRIPTION || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_DESCRIPTION', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Institut Inhaber"
                      fullWidth
                      value={tempConfig.INSTITUTE_OWNER || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_OWNER', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Wissenschaftliche Leitung"
                      fullWidth
                      value={tempConfig.INSTITUTE_SCIENTIFIC_LEAD || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_SCIENTIFIC_LEAD', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Adresse"
                      fullWidth
                      value={tempConfig.INSTITUTE_ADDRESS || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_ADDRESS', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Ort"
                      fullWidth
                      value={tempConfig.INSTITUTE_CITY || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_CITY', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Telefon 1"
                      fullWidth
                      value={tempConfig.INSTITUTE_PHONE1 || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_PHONE1', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Telefon 2"
                      fullWidth
                      value={tempConfig.INSTITUTE_PHONE2 || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_PHONE2', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="E-Mail"
                      fullWidth
                      value={tempConfig.INSTITUTE_EMAIL || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_EMAIL', e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Website"
                      fullWidth
                      value={tempConfig.INSTITUTE_WEBSITE || ''}
                      onChange={(e) => handleConfigChange('INSTITUTE_WEBSITE', e.target.value)}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Anwendungseinstellungen */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Anwendungseinstellungen
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      label="Standard Session Gültigkeit (Tage)"
                      type="number"
                      fullWidth
                      value={tempConfig.DEFAULT_SESSION_EXPIRY || ''}
                      onChange={(e) => handleConfigChange('DEFAULT_SESSION_EXPIRY', e.target.value)}
                      InputProps={{ inputProps: { min: 1, max: 365 } }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* E-Mail Templates */}
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  E-Mail Templates
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Die E-Mail-Templates können in der Zukunft hier bearbeitet werden. 
                  Aktuell werden die Templates aus der Backend-Konfiguration verwendet.
                </Typography>
                <Button 
                  variant="outlined" 
                  disabled
                  fullWidth
                >
                  Templates bearbeiten (Bald verfügbar)
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </>
    );
  };

  // Render-Funktion für den Erinnerungen-Tab
  const renderReminders = () => {
    return (
      <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h6">
            Unbeantwortete Fragebögen
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchPendingSessions}
            disabled={pendingLoading}
          >
            Aktualisieren
          </Button>
        </Box>

        {/* Filter */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              Filter
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Mindestens X Tage alt"
                  type="number"
                  fullWidth
                  size="small"
                  value={reminderFilter.daysSinceCreated}
                  onChange={(e) => setReminderFilter({ ...reminderFilter, daysSinceCreated: e.target.value })}
                  helperText="Nur Sessions älter als X Tage"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Läuft ab in X Tagen"
                  type="number"
                  fullWidth
                  size="small"
                  value={reminderFilter.daysUntilExpiry}
                  onChange={(e) => setReminderFilter({ ...reminderFilter, daysUntilExpiry: e.target.value })}
                  helperText="Nur Sessions die bald ablaufen"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={fetchPendingSessions}
                  disabled={pendingLoading}
                >
                  Filter anwenden
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Aktionen */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SendIcon />}
            onClick={() => sendReminders(selectedReminders.length > 0 ? selectedReminders : null)}
            disabled={sendingReminders || pendingSessions.length === 0}
          >
            {selectedReminders.length > 0
              ? `Erinnerung an ${selectedReminders.length} Ausgewählte senden`
              : `Erinnerung an alle (${pendingSessions.length}) senden`}
          </Button>
          {pendingSessions.length > 0 && (
            <Button
              variant="outlined"
              onClick={selectAllReminders}
            >
              {selectedReminders.length === pendingSessions.length ? 'Auswahl aufheben' : 'Alle auswählen'}
            </Button>
          )}
        </Box>

        {/* Loading */}
        {pendingLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Tabelle */}
        {!pendingLoading && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <input
                      type="checkbox"
                      checked={selectedReminders.length === pendingSessions.length && pendingSessions.length > 0}
                      onChange={selectAllReminders}
                    />
                  </TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>E-Mail</TableCell>
                  <TableCell>Erstellt</TableCell>
                  <TableCell>Alter (Tage)</TableCell>
                  <TableCell>Läuft ab in</TableCell>
                  <TableCell>Erinnert</TableCell>
                  <TableCell>Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pendingSessions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
                        <Typography color="text.secondary">
                          Keine unbeantworteten Fragebögen gefunden
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          onClick={fetchPendingSessions}
                        >
                          Neu laden
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  pendingSessions.map((session) => (
                    <TableRow
                      key={session.session_id}
                      sx={{
                        backgroundColor: selectedReminders.includes(session.session_id) ? 'action.selected' : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={selectedReminders.includes(session.session_id)}
                          onChange={() => toggleReminderSelection(session.session_id)}
                        />
                      </TableCell>
                      <TableCell>
                        {session.patient.first_name} {session.patient.last_name}
                        {session.patient.patient_id && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            ID: {session.patient.patient_id}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{session.patient.email}</TableCell>
                      <TableCell>{formatDate(session.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${session.days_old} Tage`}
                          size="small"
                          color={session.days_old > 5 ? 'warning' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        {session.days_until_expiry !== null ? (
                          <Chip
                            label={`${session.days_until_expiry} Tage`}
                            size="small"
                            color={session.days_until_expiry <= 2 ? 'error' : 'default'}
                          />
                        ) : '-'}
                      </TableCell>
                      <TableCell>
                        {session.reminder_count
                          ? (
                            <Tooltip title={`Zuletzt: ${formatDate(session.last_reminder_at)}`}>
                              <Chip label={`${session.reminder_count}×`} size="small" color="warning" variant="outlined" />
                            </Tooltip>
                          )
                          : <Typography variant="caption" color="text.secondary">–</Typography>}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Einzelne Erinnerung senden">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => sendReminders([session.session_id])}
                            disabled={sendingReminders}
                          >
                            <SendIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Info-Box */}
        <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="body2" color="info.contrastText">
            <strong>Hinweis:</strong> Erinnerungs-E-Mails werden an alle Patienten gesendet, deren Fragebögen noch nicht ausgefüllt wurden.
            Die E-Mails verwenden das Follow-up-Template aus den Einstellungen.
          </Typography>
        </Box>
      </>
    );
  };

  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 1, sm: 4 }, mb: 4, px: { xs: 1, sm: 3 } }}>
      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
              fontWeight: 'bold'
            }}
          >
            OSP-Admin
          </Typography>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<LogoutIcon />}
            onClick={handleLogout}
            sx={{ minWidth: 'auto' }}
          >
            Abmelden
          </Button>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="admin tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTabs-flexContainer': { justifyContent: { md: 'space-between' } },
              '& .MuiTab-root': {
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                minHeight: { xs: 56, sm: 64 },
                minWidth: { xs: 96, md: 160 },
                px: { xs: 1.5, sm: 2 }
              }
            }}
          >
            <Tab 
              label="Patienten" 
              id="admin-tab-0"
              icon={<PersonIcon />}
              iconPosition="top"
              sx={{ display: { xs: 'flex', sm: 'flex' } }}
            />
            <Tab 
              label="Fragebögen" 
              id="admin-tab-1" 
              disabled={!selectedPatient}
              icon={<AssignmentIcon />}
              iconPosition="top"
            />
            <Tab 
              label="Auswertung" 
              id="admin-tab-2"
              icon={<ChartIcon />}
              iconPosition="top"
            />
            <Tab
              label="Einstellungen"
              id="admin-tab-3"
              icon={<SettingsIcon />}
              iconPosition="top"
            />
            <Tab
              label="Erinnerungen"
              id="admin-tab-4"
              icon={<ReminderIcon />}
              iconPosition="top"
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          {renderPatientsList()}
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          {renderSessionsList()}
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <OSPAuswertung />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          {renderSettings()}
        </TabPanel>
        <TabPanel value={tabValue} index={4}>
          {renderReminders()}
        </TabPanel>
      </Paper>

      {/* Dialog: Neuer Patient */}
      <Dialog 
        open={openNewPatientDialog} 
        onClose={() => setOpenNewPatientDialog(false)}
        fullScreen={window.innerWidth < 600}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Neuen Patienten erstellen</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Geben Sie die Daten des neuen Patienten ein. Es wird automatisch ein neuer Fragebogen-Link erstellt.
          </DialogContentText>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Patienten-ID (optional)"
                fullWidth
                variant="outlined"
                value={newPatient.patient_id}
                onChange={(e) => setNewPatient({ ...newPatient, patient_id: e.target.value })}
                helperText="Alphanumerische ID für den Patienten"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Sprache"
                fullWidth
                variant="outlined"
                value={newPatient.language}
                onChange={(e) => setNewPatient({ ...newPatient, language: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="DE">Deutsch</option>
                <option value="EN">English</option>
                <option value="FR">Français</option>
                <option value="IT">Italiano</option>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                label="Vorname"
                fullWidth
                variant="outlined"
                required
                value={newPatient.first_name}
                onChange={(e) => setNewPatient({ ...newPatient, first_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nachname"
                fullWidth
                variant="outlined"
                required
                value={newPatient.last_name}
                onChange={(e) => setNewPatient({ ...newPatient, last_name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="E-Mail"
                type="email"
                fullWidth
                variant="outlined"
                required
                value={newPatient.email}
                onChange={(e) => setNewPatient({ ...newPatient, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Telefon (optional)"
                fullWidth
                variant="outlined"
                value={newPatient.phone}
                onChange={(e) => setNewPatient({ ...newPatient, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Geburtsdatum"
                type="date"
                fullWidth
                variant="outlined"
                required
                value={newPatient.date_of_birth}
                onChange={(e) => setNewPatient({ ...newPatient, date_of_birth: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Format: YYYY-MM-DD"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                label="Geschlecht (optional)"
                fullWidth
                variant="outlined"
                value={newPatient.gender}
                onChange={(e) => setNewPatient({ ...newPatient, gender: e.target.value })}
                SelectProps={{ native: true }}
              >
                <option value="">Nicht angegeben</option>
                <option value="M">Männlich</option>
                <option value="F">Weiblich</option>
                <option value="D">Divers</option>
                <option value="X">Andere</option>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Behandelnder Therapeut (optional)"
                fullWidth
                variant="outlined"
                value={newPatient.doctor_id}
                onChange={(e) => setNewPatient({ ...newPatient, doctor_id: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notizen (optional)"
                fullWidth
                variant="outlined"
                multiline
                rows={3}
                value={newPatient.notes}
                onChange={(e) => setNewPatient({ ...newPatient, notes: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setOpenNewPatientDialog(false)}
            fullWidth={window.innerWidth < 600}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={createPatient}
            variant="contained"
            disabled={loading}
            fullWidth={window.innerWidth < 600}
          >
            {loading ? <CircularProgress size={24} /> : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Patient bearbeiten */}
      <Dialog 
        open={openEditPatientDialog} 
        onClose={() => setOpenEditPatientDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Patient bearbeiten</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Bearbeiten Sie die Daten des Patienten.
          </DialogContentText>
          {editPatient && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Patienten-ID (optional)"
                  fullWidth
                  variant="outlined"
                  value={editPatient.patient_id || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, patient_id: e.target.value })}
                  helperText="Alphanumerische ID für den Patienten"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Sprache"
                  fullWidth
                  variant="outlined"
                  value={editPatient.language || 'DE'}
                  onChange={(e) => setEditPatient({ ...editPatient, language: e.target.value })}
                  SelectProps={{ native: true }}
                >
                  <option value="DE">Deutsch</option>
                  <option value="EN">English</option>
                  <option value="FR">Français</option>
                  <option value="IT">Italiano</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Vorname"
                  fullWidth
                  variant="outlined"
                  value={editPatient.first_name || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Nachname"
                  fullWidth
                  variant="outlined"
                  value={editPatient.last_name || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, last_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="E-Mail"
                  type="email"
                  fullWidth
                  variant="outlined"
                  value={editPatient.email || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Telefon (optional)"
                  fullWidth
                  variant="outlined"
                  value={editPatient.phone || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Geburtsdatum"
                  type="date"
                  fullWidth
                  variant="outlined"
                  value={editPatient.date_of_birth || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, date_of_birth: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  helperText="Format: YYYY-MM-DD"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Geschlecht (optional)"
                  fullWidth
                  variant="outlined"
                  value={editPatient.gender || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, gender: e.target.value })}
                  SelectProps={{ native: true }}
                >
                  <option value="">Nicht angegeben</option>
                  <option value="M">Männlich</option>
                  <option value="F">Weiblich</option>
                  <option value="D">Divers</option>
                  <option value="X">Andere</option>
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Behandelnder Therapeut (optional)"
                  fullWidth
                  variant="outlined"
                  value={editPatient.doctor_id || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, doctor_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Notizen (optional)"
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  value={editPatient.notes || ''}
                  onChange={(e) => setEditPatient({ ...editPatient, notes: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button 
            onClick={() => setOpenEditPatientDialog(false)}
            fullWidth={window.innerWidth < 600}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={updatePatient}
            variant="contained"
            disabled={loading}
            fullWidth={window.innerWidth < 600}
          >
            {loading ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Neuer Fragebogen */}
      <Dialog open={openNewSessionDialog} onClose={() => setOpenNewSessionDialog(false)}>
        <DialogTitle>Neuen Fragebogen erstellen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Erstellen Sie einen neuen Fragebogen-Link für den Patienten.
          </DialogContentText>
          <TextField
            margin="dense"
            label="Gültigkeitsdauer (Tage)"
            type="number"
            fullWidth
            variant="outlined"
            value={newSession.expiresDays}
            onChange={(e) => setNewSession({ ...newSession, expiresDays: parseInt(e.target.value) || DEFAULT_EXPIRES_DAYS })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNewSessionDialog(false)}>Abbrechen</Button>
          <Button 
            onClick={createSession}
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Erstellen'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: Patient Import */}
      <Dialog 
        open={importDialog} 
        onClose={() => {
          setImportDialog(false);
          setImportFile(null);
          setImportResult(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Patienten importieren</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Importieren Sie Patienten aus einer CSV-Datei. Die Datei sollte Spalten für "name", "email" und "alter" enthalten.
            Duplikate werden anhand der E-Mail-Adresse erkannt und übersprungen.
          </DialogContentText>
          
          <Button
            variant="outlined"
            component="label"
            fullWidth
            sx={{ mb: 2 }}
          >
            {importFile ? importFile.name : 'CSV-Datei auswählen'}
            <input
              type="file"
              accept=".csv"
              hidden
              onChange={(e) => setImportFile(e.target.files[0])}
            />
          </Button>
          
          {importResult && (
            <Alert 
              severity={importResult.errors > 0 ? "warning" : "success"} 
              sx={{ mt: 2 }}
            >
              <strong>Import abgeschlossen:</strong>
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>{importResult.imported} Patienten importiert</li>
                <li>{importResult.duplicates} Duplikate übersprungen</li>
                {importResult.errors > 0 && <li>{importResult.errors} Fehler</li>}
              </ul>
              {importResult.error_details && importResult.error_details.length > 0 && (
                <>
                  <strong>Fehlerdetails:</strong>
                  <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                    {importResult.error_details.slice(0, 5).map((error, index) => (
                      <li key={index}>{error}</li>
                    ))}
                    {importResult.error_details.length > 5 && (
                      <li>... und {importResult.error_details.length - 5} weitere</li>
                    )}
                  </ul>
                </>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setImportDialog(false);
            setImportFile(null);
            setImportResult(null);
          }}>
            Schließen
          </Button>
          <Button 
            onClick={handleImport}
            variant="contained"
            disabled={!importFile || loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Importieren'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar: Link kopiert */}
      <Snackbar
        open={linkCopied}
        autoHideDuration={3000}
        onClose={() => setLinkCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setLinkCopied(false)} severity="success">
          Link wurde in die Zwischenablage kopiert!
        </Alert>
      </Snackbar>

      {/* Modal: Fragebogen anzeigen */}
      <Modal
        open={openQuestionnaireModal}
        onClose={handleCloseModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openQuestionnaireModal}>
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '95%', sm: '90%', md: '80%', lg: '70%' },
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Modal Header */}
            <Box 
              className="no-print"
              sx={{ 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
              <Typography variant="h6">
                Fragebogen Details
                {selectedSession && (
                  <Typography variant="caption" display="block" color="text.secondary">
                    Session: {selectedSession.session_id}
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Als PDF herunterladen">
                  <IconButton onClick={handleDownloadPDF} size="small">
                    <PdfIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Drucken">
                  <IconButton onClick={handlePrint} size="small">
                    <PrintIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bericht an Admin senden">
                  <IconButton onClick={handleSendReport} size="small">
                    <EmailIcon />
                  </IconButton>
                </Tooltip>
                <IconButton onClick={handleCloseModal} size="small">
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            {/* Modal Content */}
            <Box 
              id="questionnaire-modal-content"
              sx={{ 
                flex: 1, 
                overflow: 'auto', 
                p: { xs: 2, sm: 3 }
              }}
            >
              <Box id="questionnaire-content">
                {renderQuestionnaireDetails()}
              </Box>
            </Box>

            {/* Modal Footer */}
            <Box 
              className="no-print"
              sx={{ 
                p: 2, 
                borderTop: 1, 
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
              }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<PdfIcon />}
                  onClick={handleDownloadPDF}
                  size="small"
                >
                  PDF Download
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={handlePrint}
                  size="small"
                >
                  Drucken
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={handleSendReport}
                  size="small"
                >
                  An Admin senden
                </Button>
              </Box>
              <Button
                variant="contained"
                onClick={handleCloseModal}
              >
                Schließen
              </Button>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Container>
  );
};

export default OSPAdmin;